import { useEffect, useMemo, useState } from 'react'

type StorageValueFactory<T> = () => T

export interface LocalStorageStateOptions {
  scope?: string
  storage?: Storage
}

const isBrowser = () => typeof window !== 'undefined'

const withScope = (key: string, scope?: string) =>
  scope ? `${scope}:${key}` : key

export function readLocalStorage<T>(
  key: string,
  fallback: T,
  options?: LocalStorageStateOptions,
): T {
  const storage = options?.storage ?? (isBrowser() ? window.localStorage : null)
  if (!storage) return fallback

  try {
    const raw = storage.getItem(withScope(key, options?.scope))
    if (raw === null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function writeLocalStorage<T>(
  key: string,
  value: T,
  options?: LocalStorageStateOptions,
) {
  const storage = options?.storage ?? (isBrowser() ? window.localStorage : null)
  if (!storage) return

  try {
    storage.setItem(withScope(key, options?.scope), JSON.stringify(value))
  } catch {
    // Ignore storage quota/private mode errors.
  }
}

export function removeLocalStorage(
  key: string,
  options?: LocalStorageStateOptions,
) {
  const storage = options?.storage ?? (isBrowser() ? window.localStorage : null)
  if (!storage) return

  try {
    storage.removeItem(withScope(key, options?.scope))
  } catch {
    // Ignore private mode/storage errors.
  }
}

export function useLocalStorageState<T>(
  key: string,
  defaultValue: T | StorageValueFactory<T>,
  options?: LocalStorageStateOptions,
) {
  const scopedKey = useMemo(() => withScope(key, options?.scope), [key, options?.scope])

  const [state, setState] = useState<T>(() => {
    const fallback =
      typeof defaultValue === 'function'
        ? (defaultValue as StorageValueFactory<T>)()
        : defaultValue

    if (!isBrowser()) return fallback

    try {
      const raw = window.localStorage.getItem(scopedKey)
      if (raw === null) return fallback
      return JSON.parse(raw) as T
    } catch {
      return fallback
    }
  })

  useEffect(() => {
    if (!isBrowser()) return

    try {
      window.localStorage.setItem(scopedKey, JSON.stringify(state))
    } catch {
      // Ignore storage quota/private mode errors.
    }
  }, [scopedKey, state])

  return [state, setState] as const
}
